import React, { useState, useEffect } from 'react';
import { Card, Form, Button, Alert } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { connect } from 'react-redux';
import { getBankAccounts, createWithdraw } from '../../api/api';
import PageTitle from '../layouts/PageTitle';
import Loading from '../components/Loading';
import swal from 'sweetalert2';

const Withdraw = ({ auth }) => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [bankAccounts, setBankAccounts] = useState([]);
    const [selectedAccount, setSelectedAccount] = useState(null);
    const [error, setError] = useState(null);
    const [formData, setFormData] = useState({
        amount: '',
        bankAccountId: '',
        medium: 'NEFT'
    });

    const WITHDRAWAL_MEDIUMS = [
        { value: 'NEFT', label: 'NEFT' },
        { value: 'RTGS', label: 'RTGS' },
        { value: 'IMPS', label: 'IMPS' },
        { value: 'crypto', label: 'Crypto' }
    ];

    useEffect(() => {
        if (!auth.isAuthenticated || !auth.token) {
            navigate('/login', { replace: true });
            return;
        }
        
        fetchBankAccounts();
    }, [auth, navigate]);

    const fetchBankAccounts = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await getBankAccounts();
            
            if (response.success) {
                const accounts = response.data || [];
                setBankAccounts(accounts);
                
                // Select primary account by default
                const primaryAccount = accounts.find(acc => acc.isPrimary && acc.status === 'active');
                const firstActiveAccount = accounts.find(acc => acc.status === 'active');
                const defaultAccount = primaryAccount || firstActiveAccount;

                if (defaultAccount) {
                    setFormData(prev => ({
                        ...prev,
                        bankAccountId: defaultAccount._id
                    }));
                    setSelectedAccount(defaultAccount);
                }
            }
        } catch (error) {
            console.error('Error fetching bank accounts:', error);
            setError(error.message);
            setBankAccounts([]);
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        if (name === 'bankAccountId') {
            const account = bankAccounts.find(acc => acc._id === value);
            setSelectedAccount(account);
        }
    };

    const validateForm = () => {
        if (!formData.amount || isNaN(formData.amount) || formData.amount <= 0) {
            swal.fire({
                icon: 'error',
                title: 'Validation Error',
                text: 'Please enter a valid amount'
            });
            return false;
        }

        if (!formData.bankAccountId) {
            swal.fire({
                icon: 'error',
                title: 'Validation Error',
                text: 'Please select a bank account'
            });
            return false;
        }

        if (!formData.medium) {
            swal.fire({
                icon: 'error',
                title: 'Validation Error',
                text: 'Please select a withdrawal medium'
            });
            return false;
        }

        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        try {
            setSubmitting(true);
            setError(null);

            const response = await createWithdraw(formData);
            console.log('Withdrawal response:', response);

            if (response.success) {
                const withdrawalData = response.data;
                
                // Show success message
                swal.fire({
                    title: '<strong>Withdrawal Submitted</strong>',
                    icon: 'success',
                    html: `
                        <div style="text-align: left; padding: 10px;">
                            <div style="color: #2bc155; margin-bottom: 15px; font-weight: 500;">
                                ${response.message}
                            </div>
                            <table class="table table-striped" style="margin-bottom: 15px;">
                                <tr>
                                    <td><strong>Withdrawal ID:</strong></td>
                                    <td class="text-end">${withdrawalData.withdrawalId}</td>
                                </tr>
                                <tr>
                                    <td><strong>Reference:</strong></td>
                                    <td class="text-end">${withdrawalData.reference}</td>
                                </tr>
                                <tr>
                                    <td><strong>Amount:</strong></td>
                                    <td class="text-end">${Number(withdrawalData.amount).toLocaleString('en-IN', {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2
                                    })}</td>
                                </tr>
                                <tr>
                                    <td><strong>Medium:</strong></td>
                                    <td class="text-end">${withdrawalData.medium}</td>
                                </tr>
                                <tr>
                                    <td><strong>Bank Account:</strong></td>
                                    <td class="text-end">${selectedAccount.accountNumber}</td>
                                </tr>
                                <tr>
                                    <td><strong>Status:</strong></td>
                                    <td class="text-end">
                                        <span class="badge badge-${
                                            withdrawalData.status === 'pending' ? 'warning' : 
                                            withdrawalData.status === 'approved' ? 'success' : 
                                            withdrawalData.status === 'rejected' ? 'danger' : 'secondary'
                                        }">
                                            ${withdrawalData.status.toUpperCase()}
                                        </span>
                                    </td>
                                </tr>
                            </table>
                            <div style="color: #666; font-size: 12px; text-align: center;">
                                Please save this information for future reference
                            </div>
                        </div>
                    `,
                    showCloseButton: true,
                    showCancelButton: false,
                    focusConfirm: false,
                    confirmButtonText: 'OK',
                    confirmButtonColor: '#2bc155',
                    customClass: {
                        container: 'custom-swal-container',
                        popup: 'custom-swal-popup',
                        header: 'custom-swal-header',
                        title: 'custom-swal-title',
                        closeButton: 'custom-swal-close',
                        content: 'custom-swal-content',
                        confirmButton: 'btn btn-success',
                    }
                });

                // Reset form
                setFormData({
                    amount: '',
                    bankAccountId: selectedAccount?._id || '',
                    medium: 'NEFT'
                });

            } else {
                // Handle verification status error
                if (response.verificationStatus) {
                    let errorMessage = response.message || 'Verification required for withdrawals';
                    
                    // Only add verification details if they're not already verified
                    const { emailVerified, mobileVerified } = response.verificationStatus;
                    const verificationDetails = [];

                    if (!emailVerified) {
                        verificationDetails.push('Email verification pending');
                    }
                    if (!mobileVerified) {
                        verificationDetails.push('Mobile verification pending');
                    }

                    if (!verificationDetails.length) {
                        verificationDetails.push('KYC verification pending');
                    }

                    errorMessage += '<br><br>Pending Verifications:<br>' + verificationDetails.map(detail => `• ${detail}`).join('<br>');

                    swal.fire({
                        icon: 'warning',
                        title: 'Verification Required',
                        html: errorMessage,
                        confirmButtonColor: '#ffb800',
                        showCancelButton: true,
                        cancelButtonText: 'Close',
                        confirmButtonText: 'Complete KYC',
                        reverseButtons: true
                    }).then((result) => {
                        if (result.isConfirmed) {
                            navigate('/kyc');
                        }
                    });
                } else {
                    // Handle other types of errors
                    swal.fire({
                        icon: 'error',
                        title: 'Error',
                        text: response.message || 'Failed to submit withdrawal request',
                        confirmButtonColor: '#ff4f4f'
                    });
                }
            }
        } catch (error) {
            console.error('Error creating withdrawal:', error);
            setError(error.message);
            swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'An unexpected error occurred. Please try again later.',
                confirmButtonColor: '#ff4f4f'
            });
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) return <Loading />;

    return (
        <div className="withdraw-page">
            <PageTitle activeMenu="Withdraw" motherMenu="Wallet" />

            <div className="row g-3">
                <div className="col-xxl-4 col-xl-4 col-lg-5">
                    <div className="sticky-top" style={{ top: '20px', zIndex: 1 }}>
                        <Card>
                            <Card.Header>
                                <Card.Title>Selected Bank Account</Card.Title>
                            </Card.Header>
                            <Card.Body>
                                {selectedAccount ? (
                                    <div className="bank-account-details">
                                        <h5 className="text-primary">{selectedAccount.bankName}</h5>
                                        <div className="mt-3">
                                            <p className="mb-2 d-flex justify-content-between">
                                                <span className="text-muted">Account:</span>
                                                <span className="font-w600">{selectedAccount.accountNumber}</span>
                                            </p>
                                            <p className="mb-2 d-flex justify-content-between">
                                                <span className="text-muted">IFSC:</span>
                                                <span className="font-w600">{selectedAccount.ifscCode}</span>
                                            </p>
                                            <p className="mb-0 d-flex justify-content-between">
                                                <span className="text-muted">Holder:</span>
                                                <span className="font-w600">{selectedAccount.accountHolderName}</span>
                                            </p>
                                        </div>
                                    </div>
                                ) : (
                                    <Alert variant="warning">
                                        No bank account selected
                                    </Alert>
                                )}
                            </Card.Body>
                        </Card>
                    </div>
                </div>

                <div className="col-xxl-8 col-xl-8 col-lg-7">
                    <Card>
                        <Card.Header>
                            <Card.Title>Withdraw Funds</Card.Title>
                        </Card.Header>
                        <Card.Body>
                            {bankAccounts.length === 0 ? (
                                <Alert variant="warning">
                                    No bank accounts available. Please add a bank account first.
                                </Alert>
                            ) : (
                                <Form onSubmit={handleSubmit} className="withdraw-form">
                                    <Form.Group className="mb-4">
                                        <Form.Label>Bank Account</Form.Label>
                                        <Form.Select
                                            name="bankAccountId"
                                            value={formData.bankAccountId}
                                            onChange={handleInputChange}
                                            required
                                            className="form-control-lg"
                                        >
                                            <option value="">Select Bank Account</option>
                                            {bankAccounts.map(account => (
                                                <option key={account._id} value={account._id}>
                                                    {account.bankName} - {account.accountNumber}
                                                    {account.isPrimary ? ' (Primary)' : ''}
                                                </option>
                                            ))}
                                        </Form.Select>
                                    </Form.Group>

                                    <Form.Group className="mb-4">
                                        <Form.Label>Amount</Form.Label>
                                        <div className="amount-input-wrapper">
                                            <Form.Control
                                                type="number"
                                                name="amount"
                                                value={formData.amount}
                                                onChange={handleInputChange}
                                                required
                                                className="form-control-lg"
                                                placeholder="Enter amount to withdraw"
                                            />
                                        </div>
                                    </Form.Group>

                                    <Form.Group className="mb-4">
                                        <Form.Label>Withdrawal Medium</Form.Label>
                                        <Form.Select
                                            name="medium"
                                            value={formData.medium}
                                            onChange={handleInputChange}
                                            required
                                            className="form-control-lg"
                                        >
                                            {WITHDRAWAL_MEDIUMS.map(medium => (
                                                <option key={medium.value} value={medium.value}>
                                                    {medium.label}
                                                </option>
                                            ))}
                                        </Form.Select>
                                        <Form.Text className="text-muted">
                                            Select your preferred withdrawal method
                                        </Form.Text>
                                    </Form.Group>

                                    <div className="text-center mt-4">
                                        <Button 
                                            type="submit" 
                                            variant="primary" 
                                            size="lg"
                                            disabled={submitting || !selectedAccount}
                                        >
                                            {submitting ? 'Processing...' : 'Submit Withdrawal'}
                                        </Button>
                                    </div>
                                </Form>
                            )}
                        </Card.Body>
                    </Card>
                </div>
            </div>

            <style jsx>{`
                .withdraw-page {
                    min-height: calc(100vh - 200px);
                    padding-bottom: 2rem;
                }
                .bank-account-details {
                    background: #f8f9fa;
                    padding: 1rem;
                    border-radius: 0.5rem;
                }
                .amount-input-wrapper {
                    max-width: 400px;
                }
                @media (max-width: 991px) {
                    .sticky-top {
                        position: relative !important;
                        top: 0 !important;
                        margin-bottom: 1rem;
                    }
                }
                /* ... rest of the styles from Deposit page ... */
            `}</style>
        </div>
    );
};

const mapStateToProps = (state) => ({
    auth: state.auth
});

export default connect(mapStateToProps)(Withdraw); 